import { useCallback } from "react";
import { Controller, FieldValues, UseControllerProps } from "react-hook-form";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import type { SxProps } from "@mui/material/styles";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { add, format, isValid, parse } from "date-fns";

import { isDateStringValid } from "utils/common";

type DateStringFormFieldProps = {
  sx?: SxProps;
  label: string;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  PopperProps?: any;
  allowFutureDateOnly?: boolean;
  quickOptions?: string[];
  quickOptionsPlacement?: "inline" | "below";
  quickOptionBaseDate?: Date | null;
  triggerInputOnBlur?: boolean;
  onQuickOptionClick?: (
    value: string,
    currentDate: Date | null,
    baseDate?: Date | null
  ) => Date | null;
  onBlur?: () => void;
  additionalValidation?: (value: any) => boolean | string;
};

type RhfDateStringFormFieldProps<T extends FieldValues> =
  DateStringFormFieldProps & UseControllerProps<T>;

export const DATE_MASK = "MM/dd/yyyy";

export const DEFAULT_DATE_QUICK_OPTIONS = [
  "+1",
  "+2",
  "+3",
  "+5",
  "+7",
  "+14",
  "+30"
];

export const defaultOnQuickOptionClick = (value: string) => {
  const days = parseInt(value.replace("+", ""));
  if (!days) return null;
  return add(new Date(), { days });
};

export const WARRANTY_QUICK_OPTIONS = ["+1yr", "+2yr", "+3yr", "+4yr", "+5yr"];
export const SERVICE_PLAN_QUICK_OPTIONS = ["+1yr", "+2yr", "+3yr", "+5yr"];
export const TRIAL_PERIOD_QUICK_OPTIONS = [
  "+30d",
  "+45d",
  "+60d",
  "+90d",
  "+6mo",
  "+1yr"
];

export const warrantyQuickOptionHandler = (
  value: string,
  _currentDate?: Date | null,
  baseDate?: Date | null
): Date | null => {
  const startDate = baseDate ?? new Date();
  const num = parseInt(value.replace("+", ""));
  if (!num) return null;

  if (value.endsWith("yr")) return add(startDate, { years: num });
  if (value.endsWith("mo")) return add(startDate, { months: num });
  if (value.endsWith("d")) return add(startDate, { days: num });
  return null;
};

export default function DateStringFormField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  autoFocus,
  PopperProps,
  placeholder = DATE_MASK.toUpperCase(),
  readOnly = false,
  disabled = false,
  allowFutureDateOnly,
  sx,
  triggerInputOnBlur,
  onBlur: onBlurCallback,
  additionalValidation,
  quickOptions,
  quickOptionsPlacement = "inline",
  quickOptionBaseDate,
  onQuickOptionClick
}: RhfDateStringFormFieldProps<T>) {
  const showInline =
    quickOptionsPlacement === "inline" && Boolean(quickOptions?.length);
  const showBelow =
    quickOptionsPlacement === "below" && Boolean(quickOptions?.length);
  const validate = useCallback(
    (value: any) => {
      if (readOnly || disabled) return true;

      const date = value && parse(value, DATE_MASK, new Date());
      if (date && !isValid(date)) {
        return `The ${label || "date"} is not valid (MM/DD/YYYY).`;
      }
      if (date && isValid(date)) {
        const year = date.getFullYear();
        if (year < 1900 || year > 2100) {
          return `The ${label || "date"} has an invalid year.`;
        }
      }
      if (date && allowFutureDateOnly && date <= new Date()) {
        return "Date should be in the future";
      }

      if (additionalValidation) {
        return additionalValidation?.(date);
      }

      return true;
    },
    [allowFutureDateOnly, readOnly, disabled, label, additionalValidation]
  );

  return (
    <Box
      display="flex"
      {...(showBelow ? { flexDirection: "column" } : { alignItems: "center" })}
      sx={{
        pr: 4,
        ...sx
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Controller
          control={control}
          name={name}
          rules={{
            ...(required
              ? {
                  required: `${label || "date"} is required`
                }
              : {}),
            validate
          }}
          render={({
            field: { onBlur, name, value, ...field },
            fieldState
          }) => {
            function handleNewDateValue(newValue: any, field: any) {
              if (newValue === null) {
                field.onChange("");
              } else if (!isNaN(newValue) && newValue instanceof Date) {
                field.onChange(format(newValue, DATE_MASK));
              } else {
                field.onChange(newValue !== "" ? "Invalid Date" : newValue);
              }
            }

            const currentDate =
              value && isValid(parse(value, DATE_MASK, new Date()))
                ? parse(value, DATE_MASK, new Date())
                : null;

            return (
              <>
                <DesktopDatePicker
                  {...field}
                  views={["year", "month", "day"]}
                  value={value ? parse(value, DATE_MASK, new Date()) : null}
                  disabled={disabled}
                  slotProps={{
                    textField: {
                      required,
                      placeholder,
                      disabled,
                      InputProps: {
                        autoFocus,
                        readOnly,
                        spellCheck: false,
                        onBlur: triggerInputOnBlur ? onBlurCallback : undefined,
                        endAdornment: !showInline ? undefined : (
                          <Stack
                            direction="row"
                            justifyContent="flex-end"
                            alignItems="center"
                            mr={-1.6}
                            width="220px"
                          >
                            {quickOptions!.map((option) => (
                              <ToggleButton
                                key={option}
                                value={option}
                                size="small"
                                sx={{
                                  fontSize: "12px",
                                  px: 0.75,
                                  borderRadius: 0
                                }}
                                onClick={() => {
                                  if (!onQuickOptionClick) return;
                                  const newValue = onQuickOptionClick(
                                    option,
                                    currentDate,
                                    quickOptionBaseDate
                                  );
                                  handleNewDateValue(newValue, field);
                                  onBlur();
                                  onBlurCallback?.();
                                }}
                              >
                                {option}
                              </ToggleButton>
                            ))}
                          </Stack>
                        )
                      },
                      onBlur,
                      name,
                      error: !!fieldState.error,
                      helperText: fieldState.error?.message
                    },
                    popper: PopperProps,
                    inputAdornment: {
                      position: "start",
                      sx: {
                        display: readOnly ? "none" : undefined,
                        marginRight: showInline ? "-13px" : undefined
                      }
                    }
                  }}
                  onChange={(newValue: any) => {
                    if (isDateStringValid(newValue) || !newValue) {
                      handleNewDateValue(newValue, field);
                      onBlurCallback?.();
                    }
                  }}
                  label={label}
                  onAccept={(newValue) => {
                    if (isDateStringValid(newValue) || !newValue) {
                      handleNewDateValue(newValue, field);
                      onBlur();
                      onBlurCallback?.();
                    }
                  }}
                />
                {showBelow && (
                  <Stack direction="row" mt={0.5} flexWrap="wrap">
                    {quickOptions!.map((option) => (
                      <ToggleButton
                        key={option}
                        value={option}
                        size="small"
                        sx={{
                          fontSize: "12px",
                          px: 0.75
                        }}
                        onClick={() => {
                          if (!onQuickOptionClick) return;
                          const newValue = onQuickOptionClick(
                            option,
                            currentDate,
                            quickOptionBaseDate
                          );
                          handleNewDateValue(newValue, field);
                          onBlur();
                          onBlurCallback?.();
                        }}
                      >
                        {option}
                      </ToggleButton>
                    ))}
                  </Stack>
                )}
              </>
            );
          }}
        />
      </LocalizationProvider>
    </Box>
  );
}
